/**
 * DirectoryScreen — the Connectors surface.
 * max-width 1080, pad 32/24/80; title + "New integration" (softknack);
 * search + group-by select + filter chips; grouped responsive card grid.
 */
import React from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../theme';
import { Plus } from '../../icons';
import { Button, FilterChip, SearchInput, Select } from '../../components';
import { useBridge } from '../../store/bridgeStore';
import { useDirectory } from './useDirectory';
import ConnectorCard from './ConnectorCard';

const GROUP_OPTIONS = [
  { value: 'none', label: 'None' },
  { value: 'product', label: 'Product type' },
  { value: 'company', label: 'Company' },
  { value: 'adapter', label: 'Adapter class' },
  { value: 'status', label: 'Status' },
];

export default function DirectoryScreen() {
  const { state, actions } = useBridge();
  const { groups } = useDirectory();
  const isSoftknack = state.role === 'softknack';

  return (
    <ScrollView style={styles.scroll} contentContainerStyle={styles.inner}>
      {/* heading */}
      <View style={styles.headRow}>
        <View style={{ flex: 1 }}>
          <Text style={styles.h1}>Connectors</Text>
          <Text style={styles.sub}>
            Connect the tools your team uses, then choose which SK products may use them. No setup, no code.
          </Text>
        </View>
        {isSoftknack && (
          <Button
            label="New integration"
            onPress={() => actions.openIngest()}
            height={38}
            left={<Plus size={15} color="#fff" />}
          />
        )}
      </View>

      {/* controls */}
      <View style={styles.controls}>
        <SearchInput value={state.query} onChange={actions.setQuery} placeholder="Search integrations…" width={320} />
        <View style={{ flex: 1 }} />
        <View style={styles.groupWrap}>
          <Text style={styles.groupLabel}>GROUP</Text>
          <Select value={state.groupBy} options={GROUP_OPTIONS} onChange={v => actions.setGroupBy(v as any)} variant="pill" />
        </View>
        <View style={styles.filters}>
          <FilterChip label="All" active={state.filter === 'all'} onPress={() => actions.setFilter('all')} />
          <FilterChip label="Connected" active={state.filter === 'connected'} onPress={() => actions.setFilter('connected')} />
          <FilterChip label="Available" active={state.filter === 'available'} onPress={() => actions.setFilter('available')} />
        </View>
      </View>

      {/* groups */}
      {groups.map((g, gi) => (
        <View key={gi} style={{ marginBottom: 26 }}>
          {g.showHeader && (
            <View style={styles.groupHeader}>
              <Text style={styles.groupKey}>{g.key}</Text>
              <View style={styles.countPill}>
                <Text style={styles.countText}>{g.count}</Text>
              </View>
              <View style={styles.rule} />
            </View>
          )}
          {React.createElement(
            'div',
            { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 14, width: '100%' } },
            g.items.map(item => (
              <ConnectorCard
                key={item.id}
                item={item}
                onOpen={() => (item.draft ? actions.editInBuilder(item.id) : actions.openDetail(item.id))}
              />
            )),
          )}
        </View>
      ))}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  scroll: { flex: 1, backgroundColor: colors.paper },
  inner: { maxWidth: 1080, marginHorizontal: 'auto', paddingTop: 32, paddingHorizontal: 24, paddingBottom: 80, width: '100%' } as any,
  headRow: { flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 24 },
  h1: { fontFamily: fontFamily.sans, fontSize: fontSize.xxl, fontWeight: fontWeight.semibold, color: colors.ink, letterSpacing: -0.4, marginBottom: 4 },
  sub: { fontFamily: fontFamily.sans, color: colors.ink3, fontSize: fontSize.md },
  controls: { flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: 20 },
  groupWrap: { flexDirection: 'row', alignItems: 'center', gap: 6 },
  groupLabel: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, letterSpacing: 0.5 },
  filters: { flexDirection: 'row', gap: 8 },
  groupHeader: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 8, marginBottom: 12 },
  groupKey: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink2, textTransform: 'uppercase', letterSpacing: 0.6 },
  countPill: { minWidth: 20, height: 20, paddingHorizontal: 6, borderRadius: radii.full, backgroundColor: colors.paper3, alignItems: 'center', justifyContent: 'center' },
  countText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: colors.ink3 },
  rule: { flex: 1, height: 1, backgroundColor: colors.line },
});
